import React, { useEffect, useState, useCallback } from 'react';
import api, { formatRupiah } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Target, Trash, PencilSimple } from '@phosphor-icons/react';

export default function DashGoals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', target_amount: 1000000, description: '', active: true, start_at: '', end_at: '' });

  const toLocalISO = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const load = useCallback(async () => {
    try { const res = await api.get('/goals'); setRows(res.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startAdd = () => { setEditing('new'); setForm({ title: '', target_amount: 1000000, description: '', active: true, start_at: '', end_at: '' }); };
  const startEdit = (g) => { setEditing(g.goal_id); setForm({ title: g.title, target_amount: g.target_amount, description: g.description || '', active: g.active, start_at: toLocalISO(g.start_at), end_at: toLocalISO(g.end_at) }); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.target_amount < 10000) { toast.error('Isi judul dan target min Rp 10.000'); return; }
    const payload = {
      ...form,
      start_at: form.start_at || null,
      end_at: form.end_at || null
    };
    try {
      if (editing === 'new') await api.post('/goals', payload);
      else await api.put(`/goals/${editing}`, payload);
      toast.success('Goal disimpan'); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Hapus goal ini?')) return;
    try { await api.delete(`/goals/${id}`); toast.success('Goal dihapus'); load(); } catch { toast.error('Gagal'); }
  };

  return (
    <div className="space-y-6" data-testid="dash-goals">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-1">Target</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Goals</h1>
        </div>
        {!editing && (
          <button onClick={startAdd}
            className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-4 py-2 font-bold uppercase tracking-wide shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
            data-testid="add-goal-btn">
            <Plus size={16} weight="bold" /> Tambah Goal
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6 space-y-4" data-testid="goal-form">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Judul Goal</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              placeholder="Beli mikrofon baru" required data-testid="goal-title-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Target (Rp)</label>
            <input type="number" min="10000" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: parseInt(e.target.value || '0') })}
              className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              required data-testid="goal-target-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Deskripsi</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              data-testid="goal-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Tanggal Mulai (Opsional)</label>
              <input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Tanggal Selesai (Opsional)</label>
              <input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 border-2 border-foreground" />
            Aktif (ditampilkan di halaman publik)
          </label>
          <div className="flex gap-3">
            <button type="submit" className="border-2 border-foreground bg-primary text-white px-5 py-2.5 font-bold uppercase tracking-wide shadow-brutal-sm" data-testid="goal-save-btn">Simpan</button>
            <button type="button" onClick={() => setEditing(null)} className="border-2 border-foreground bg-white px-5 py-2.5 font-bold uppercase tracking-wide shadow-brutal-sm">Batal</button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {loading ? (
          <div>Memuat...</div>
        ) : rows.length === 0 ? (
          <div className="border-2 border-dashed border-foreground p-8 text-center text-muted-foreground">Belum ada goal.</div>
        ) : (
          rows.map((g) => {
            const pct = Math.min(100, Math.round((g.current_amount / g.target_amount) * 100)) || 0;
            return (
              <div key={g.goal_id} className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6" data-testid={`goal-item-${g.goal_id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border-2 border-foreground bg-secondary flex items-center justify-center shrink-0">
                    <Target size={24} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-display text-xl font-bold">{g.title}</h3>
                      {!g.active && <span className="text-xs font-bold uppercase bg-muted border-2 border-foreground px-2 py-0.5">Nonaktif</span>}
                    </div>
                    {(g.start_at || g.end_at) && (
                      <p className="text-[10px] font-mono text-primary font-bold mb-1">
                        {g.start_at ? new Date(g.start_at).toLocaleString('id-ID') : 'Mulai Sekarang'} 
                        {' → '} 
                        {g.end_at ? new Date(g.end_at).toLocaleString('id-ID') : 'Selamanya'}
                      </p>
                    )}
                    {g.description && <p className="text-sm text-muted-foreground mb-2">{g.description}</p>}
                    <div className="h-4 border-2 border-foreground bg-muted overflow-hidden mt-2">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-2 text-sm font-bold">
                      <span>{formatRupiah(g.current_amount)} ({pct}%)</span>
                      <span>{formatRupiah(g.target_amount)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => startEdit(g)} className="w-9 h-9 border-2 border-foreground bg-accent flex items-center justify-center shadow-brutal-sm" data-testid={`goal-edit-${g.goal_id}`}>
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button onClick={() => remove(g.goal_id)} className="w-9 h-9 border-2 border-foreground bg-destructive text-white flex items-center justify-center shadow-brutal-sm" data-testid={`goal-delete-${g.goal_id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
